import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Layers,
  Package,
  RefreshCw,
  ShoppingCart,
  Users,
  AlertCircle,
  BarChart3,
  Home,
  ArrowLeft,
  TrendingUp,
  Activity,
  Info,
  Eye,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LoadingScreen from '../components/ui/LoadingScreen';
import { ProjectStatusBadge } from '../components/Projects/ProjectStatusBadge';
import { WorkStatus, WorkStatusLabels } from '../types';
import { showError } from '../components/Alerts';
import projectMonitoringService, {
  ProjectMonitoringItem,
} from '../services/projectMonitoringService';
import { formatMoney } from '../utils/format';
import { cn } from '../lib/utils';

const ROWS_PER_PAGE = 12;

// ── Helpers ────────────────────────────────────────────────────────────────────

function pct(value: number, cap = 100) {
  return Math.min(cap, Math.max(0, value));
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function currencySymbol(currency: string) {
  return currency === 'USD' ? 'U$S' : '$';
}

// ── Donut Chart SVG ────────────────────────────────────────────────────────────

const DONUT_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6',
  '#8b5cf6', '#14b8a6', '#f97316', '#ec4899', '#84cc16',
];

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

function DonutChart({ data, size = 140 }: { data: DonutSegment[]; size?: number }) {
  const r = size * 0.36;
  const cx = size / 2;
  const cy = size / 2;
  const stroke = size * 0.18;
  const circumference = 2 * Math.PI * r;

  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
          fontSize={size * 0.12} fill="var(--muted-foreground)">Sin datos</text>
      </svg>
    );
  }

  let accumulated = 0;
  const segments = data.map((d) => {
    const pctVal = d.value / total;
    const dashArray = `${pctVal * circumference} ${circumference}`;
    const dashOffset = -accumulated * circumference;
    accumulated += pctVal;
    return { ...d, dashArray, dashOffset };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.map((s, i) => (
        <circle
          key={i}
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={s.color}
          strokeWidth={stroke}
          strokeDasharray={s.dashArray}
          strokeDashoffset={s.dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
          strokeLinecap="butt"
        />
      ))}
      <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.14} fontWeight="700" fill="var(--foreground)">
        {data.length}
      </text>
      <text x={cx} y={cy + size * 0.12} textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.09} fill="var(--muted-foreground)">rubros</text>
    </svg>
  );
}

// ── Progress Bar ───────────────────────────────────────────────────────────────

function ProgressBar({
  value, max = 100, colorClass = 'bg-primary', height = 'h-2',
}: {
  value: number; max?: number; colorClass?: string; height?: string;
}) {
  const pctVal = Math.min(100, max > 0 ? (value / max) * 100 : 0);
  return (
    <div className="w-full">
      <div className={cn('w-full bg-muted rounded-full overflow-hidden', height)}>
        <div
          className={cn('h-full rounded-full transition-all duration-500', colorClass)}
          style={{ width: `${pctVal}%` }}
        />
      </div>
    </div>
  );
}

// ── Budget Bar Chart ───────────────────────────────────────────────────────────

function BudgetBars({ p }: { p: ProjectMonitoringItem }) {
  const sym = currencySymbol(p.currency);
  const maxVal = Math.max(p.budget, p.totalEstimatedCost, p.actualCost, p.totalPOCommitted, 1);

  const bars = [
    { label: 'Presupuesto', value: p.budget, color: 'bg-primary/80', textColor: 'text-primary' },
    { label: 'Estimado', value: p.totalEstimatedCost, color: 'bg-info/80', textColor: 'text-info' },
    { label: 'Real', value: p.actualCost, color: p.budgetExceeded ? 'bg-destructive' : 'bg-success/80', textColor: p.budgetExceeded ? 'text-destructive' : 'text-success' },
    { label: 'OCs', value: p.totalPOCommitted, color: 'bg-warning/80', textColor: 'text-warning' },
  ];

  return (
    <div className="space-y-3">
      {bars.map((b) => (
        <div key={b.label}>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-muted-foreground">{b.label}</span>
            <span className={cn('text-xs font-semibold tabular-nums', b.textColor)}>
              {sym} {formatMoney(b.value)}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-700', b.color)}
              style={{ width: `${Math.min(100, (b.value / maxVal) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────

function Timeline({ p }: { p: ProjectMonitoringItem }) {
  const progressWidth = pct(p.timeProgressPct);
  const todayLabel = p.overdue ? 'Vencido' : `Hoy (${p.daysRemaining}d restantes)`;

  return (
    <div className="space-y-3">
      <div className="relative w-full">
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-700',
              p.overdue ? 'bg-destructive' : progressWidth >= 80 ? 'bg-warning' : 'bg-primary'
            )}
            style={{ width: `${progressWidth}%` }}
          />
        </div>
        {!p.overdue && p.timeProgressPct > 0 && p.timeProgressPct < 100 && (
          <div
            className="absolute top-0 w-0.5 h-3 bg-foreground/70"
            style={{ left: `${progressWidth}%` }}
          />
        )}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{fmtDate(p.startDate)}</span>
        <span className={cn('font-medium', p.overdue ? 'text-destructive' : 'text-foreground')}>
          {todayLabel}
        </span>
        <span>{fmtDate(p.endDate)}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-muted/50 rounded-lg px-2 py-2">
          <p className="text-lg font-bold tabular-nums">{p.totalDays}</p>
          <p className="text-xs text-muted-foreground">días totales</p>
        </div>
        <div className={cn('rounded-lg px-2 py-2', p.overdue ? 'bg-destructive/10' : 'bg-muted/50')}>
          <p className={cn('text-lg font-bold tabular-nums', p.overdue ? 'text-destructive' : '')}>
            {p.daysElapsed}
          </p>
          <p className="text-xs text-muted-foreground">transcurridos</p>
        </div>
        <div className={cn('rounded-lg px-2 py-2', p.overdue ? 'bg-destructive/10' : 'bg-muted/50')}>
          <p className={cn('text-lg font-bold tabular-nums', p.overdue ? 'text-destructive' : '')}>
            {p.daysRemaining}
          </p>
          <p className="text-xs text-muted-foreground">{p.overdue ? 'días vencido' : 'restantes'}</p>
        </div>
      </div>
    </div>
  );
}

// ── PO Status Bar ──────────────────────────────────────────────────────────────

function POStatusBar({ p }: { p: ProjectMonitoringItem }) {
  const total = p.totalPO;
  if (total === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">Sin órdenes de compra</p>;
  }
  const bars = [
    { label: 'Borrador', value: p.draftPO, color: 'bg-muted-foreground' },
    { label: 'Enviado',  value: p.sentPO,   color: 'bg-info' },
    { label: 'Recibido', value: p.receivedPO, color: 'bg-success' },
    { label: 'Cancelado', value: p.cancelledPO, color: 'bg-destructive' },
  ];
  return (
    <div className="space-y-3">
      <div className="w-full flex rounded-full overflow-hidden h-4">
        {bars.filter(b => b.value > 0).map((b) => (
          <div
            key={b.label}
            className={cn('h-full transition-all duration-500', b.color)}
            style={{ width: `${(b.value / total) * 100}%` }}
            title={`${b.label}: ${b.value}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1">
        {bars.map((b) => (
          <div key={b.label} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', b.color)} />
              <span className="text-xs text-muted-foreground">{b.label}</span>
            </div>
            <span className="text-xs font-semibold tabular-nums">{b.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section wrapper (guide pattern: 3-col, desc left / content right) ─────────

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-1">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          {icon}
          {title}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="lg:col-span-2">{children}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── PROJECT DETAIL VIEW ───────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function ProjectDetailView({
  p,
  onBack,
}: {
  p: ProjectMonitoringItem;
  onBack: () => void;
}) {
  const sym = currencySymbol(p.currency);

  const donutData = p.workCategories.map((wc, i) => ({
    label: wc.name,
    value: Number(wc.percentage),
    color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Header */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={onBack}
                className="flex items-center gap-2 rounded-lg"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{p.name}</h1>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {p.workCode && (
                    <span className="text-xs bg-muted px-2.5 py-1 rounded-full text-muted-foreground font-medium">
                      {p.workCode}
                    </span>
                  )}
                  {p.buildingType && (
                    <span className="text-xs bg-muted px-2.5 py-1 rounded-full text-muted-foreground font-medium">
                      {p.buildingType}
                    </span>
                  )}
                  {p.projectStatus && (
                    <ProjectStatusBadge
                      status={(p.projectStatus as WorkStatus) || WorkStatus.EN_CONSTRUCCION}
                      label={WorkStatusLabels[p.projectStatus as WorkStatus] || p.projectStatus}
                      size="sm"
                    />
                  )}
                  {p.overdue && (
                    <span className="text-xs bg-destructive/10 text-destructive px-2.5 py-1 rounded-full font-medium">
                      Vencido
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-6 space-y-8 sm:px-6">

          {/* Alerts */}
          {p.alerts.length > 0 && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                <span className="text-sm font-semibold text-destructive">Alertas del proyecto</span>
              </div>
              <ul className="space-y-1">
                {p.alerts.map((a, i) => (
                  <li key={i} className="text-xs text-destructive flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-destructive mt-1.5 shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Section 1: KPIs */}
          <Section
            icon={<Activity className="w-5 h-5 text-primary" />}
            title="Indicadores Clave"
            description="Métricas principales de presupuesto, tiempo, ventas y facturación del proyecto."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Budget KPI */}
              <div className={cn(
                'rounded-xl border p-4',
                p.budgetExceeded ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Presupuesto usado</p>
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <DollarSign className="w-3.5 h-3.5 text-primary" />
                  </div>
                </div>
                <p className={cn('text-2xl font-bold tabular-nums', p.budgetExceeded ? 'text-destructive' : 'text-foreground')}>
                  {p.budgetUsedPct}%
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {sym} {formatMoney(p.actualCost)} / {sym} {formatMoney(p.budget)}
                </p>
                <div className="mt-3">
                  <ProgressBar
                    value={p.budgetUsedPct}
                    colorClass={p.budgetExceeded ? 'bg-destructive' : p.budgetUsedPct >= 80 ? 'bg-warning' : 'bg-success'}
                  />
                </div>
              </div>

              {/* Time KPI */}
              <div className={cn(
                'rounded-xl border p-4',
                p.overdue ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tiempo transcurrido</p>
                  <div className="p-1.5 rounded-lg bg-info/10">
                    <Clock className="w-3.5 h-3.5 text-info" />
                  </div>
                </div>
                <p className={cn('text-2xl font-bold tabular-nums', p.overdue ? 'text-destructive' : 'text-foreground')}>
                  {p.timeProgressPct}%
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {p.overdue ? `Vencido hace ${p.daysRemaining}d` : `${p.daysRemaining} días restantes`}
                </p>
                <div className="mt-3">
                  <ProgressBar
                    value={p.timeProgressPct}
                    colorClass={p.overdue ? 'bg-destructive' : p.timeProgressPct >= 80 ? 'bg-warning' : 'bg-primary'}
                  />
                </div>
              </div>

              {/* UF KPI */}
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Unidades vendidas</p>
                  <div className="p-1.5 rounded-lg bg-success/10">
                    <Building2 className="w-3.5 h-3.5 text-success" />
                  </div>
                </div>
                <p className="text-2xl font-bold tabular-nums">{p.soldFUPct}%</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {p.soldFU} vendidas / {p.totalFU} totales
                </p>
                <div className="mt-3">
                  <ProgressBar value={p.soldFUPct} colorClass="bg-success" />
                </div>
              </div>

              {/* Invoices KPI */}
              <div className={cn(
                'rounded-xl border p-4',
                p.pendingInvoices > 0 ? 'border-warning/40 bg-warning/5' : 'border-border bg-card'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Facturas pendientes</p>
                  <div className="p-1.5 rounded-lg bg-warning/10">
                    <FileText className="w-3.5 h-3.5 text-warning" />
                  </div>
                </div>
                <p className="text-2xl font-bold tabular-nums">{p.pendingInvoices}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {sym} {formatMoney(p.pendingInvoicesAmount)} por pagar
                </p>
                <div className="mt-3">
                  <ProgressBar
                    value={p.totalInvoices > 0 ? ((p.pendingInvoices + p.partiallyPaidInvoices) / p.totalInvoices) * 100 : 0}
                    colorClass="bg-warning"
                  />
                </div>
              </div>
            </div>
          </Section>

          <div className="border-t border-border" />

          {/* Section 2: Budget + Timeline */}
          <Section
            icon={<BarChart3 className="w-5 h-5 text-primary" />}
            title="Presupuesto y Cronograma"
            description="Análisis comparativo del presupuesto y estado de avance temporal del proyecto."
          >
            <div className="space-y-5">
              {/* Budget Analysis */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="w-4 h-4 text-primary" /></div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Análisis de presupuesto</h4>
                    <p className="text-xs text-muted-foreground">Comparación presupuesto vs real</p>
                  </div>
                </div>
                <div className="p-5">
                  <BudgetBars p={p} />
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Variación</span>
                      <span className={cn('text-sm font-semibold tabular-nums', p.budgetVariance >= 0 ? 'text-success' : 'text-destructive')}>
                        {p.budgetVariance >= 0 ? '+' : ''}{sym} {formatMoney(Math.abs(p.budgetVariance))}
                        {p.budgetVariance >= 0 ? ' disponible' : ' excedido'}
                      </span>
                    </div>
                    {p.totalM2 > 0 && (
                      <div className="flex justify-between items-center mt-1.5">
                        <span className="text-xs text-muted-foreground">Costo por m²</span>
                        <span className="text-xs font-medium tabular-nums">{sym} {formatMoney(p.costPerM2)} / m²</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-info/10"><Calendar className="w-4 h-4 text-info" /></div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Cronograma</h4>
                    <p className="text-xs text-muted-foreground">{p.overdue ? 'Proyecto vencido' : 'Estado de avance temporal'}</p>
                  </div>
                </div>
                <div className="p-5">
                  <Timeline p={p} />
                </div>
              </div>
            </div>
          </Section>

          <div className="border-t border-border" />

          {/* Section 3: Categories + POs + Invoices */}
          <Section
            icon={<Layers className="w-5 h-5 text-primary" />}
            title="Rubros, Compras y Facturación"
            description="Distribución de rubros de obra, estado de órdenes de compra y resumen de facturación."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 items-start">

              {/* Work Categories */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                    <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Rubros de obra</h4>
                    <p className="text-xs text-muted-foreground">{p.workCategories.length} rubros</p>
                  </div>
                </div>
                <div className="p-5">
                  {p.workCategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Sin rubros asignados</p>
                  ) : (
                    <div className="flex flex-col items-center gap-4">
                      <DonutChart data={donutData} size={130} />
                      <div className="w-full space-y-1.5 max-h-52 overflow-y-auto pr-1">
                        {donutData.map((d, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                              <span className="text-xs text-foreground truncate">{d.label}</span>
                            </div>
                            <span className="text-xs font-semibold tabular-nums ml-2 shrink-0">{d.value}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Orders */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <ShoppingCart className="w-4 h-4 text-warning" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Órdenes de compra</h4>
                    <p className="text-xs text-muted-foreground">{p.totalPO} órdenes</p>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <POStatusBar p={p} />
                  {p.totalPO > 0 && (
                    <div className="pt-3 border-t border-border space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Total comprometido</span>
                        <span className="font-semibold tabular-nums text-amount">{sym} {formatMoney(p.totalPOAmount)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">vs. Presupuesto</span>
                        <span className={cn('font-semibold tabular-nums', p.totalPOAmount > p.budget ? 'text-destructive' : 'text-success')}>
                          {p.budget > 0 ? `${Math.round((p.totalPOAmount / p.budget) * 100)}%` : '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoices & Sales */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-success/10">
                    <Package className="w-4 h-4 text-success" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Facturas y ventas</h4>
                    <p className="text-xs text-muted-foreground">Ingresos y egresos</p>
                  </div>
                </div>
                <div className="p-5 divide-y divide-border space-y-0">
                  <div className="pb-4 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Facturas proveedor</p>
                    {[
                      { label: 'Pendiente',   count: p.pendingInvoices,       dot: 'bg-warning' },
                      { label: 'Parcial',     count: p.partiallyPaidInvoices, dot: 'bg-info' },
                      { label: 'Pagada',      count: p.paidInvoices,          dot: 'bg-success' },
                    ].map(({ label, count, dot }) => (
                      <div key={label} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className={cn('w-2 h-2 rounded-full', dot)} />
                          <span className="text-xs text-muted-foreground">{label}</span>
                        </div>
                        <span className="text-xs font-semibold tabular-nums">{count}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs pt-1">
                      <span className="text-muted-foreground">Total facturado</span>
                      <span className="font-semibold tabular-nums">{sym} {formatMoney(p.totalInvoicesAmount)}</span>
                    </div>
                  </div>

                  <div className="pt-4 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ventas del proyecto</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Cantidad de ventas</span>
                      <span className="font-semibold tabular-nums">{p.totalSales}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Monto total</span>
                      <span className="font-semibold tabular-nums text-success">{sym} {formatMoney(p.totalSalesAmount)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">UF disponibles</span>
                      <span className="font-semibold tabular-nums text-info">{p.availableFU}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </Section>

          <div className="border-t border-border" />

          {/* Section 4: Project Data */}
          <Section
            icon={<Info className="w-5 h-5 text-primary" />}
            title="Datos del Proyecto"
            description="Información general y datos de identificación del proyecto."
          >
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Código de obra', value: p.workCode || '—' },
                  { label: 'Tipo', value: p.buildingType || '—' },
                  { label: 'Estado', value: WorkStatusLabels[p.projectStatus as WorkStatus] || p.projectStatus || '—' },
                  { label: 'Cliente', value: p.client || '—' },
                  { label: 'Ciudad', value: p.city || '—' },
                  { label: 'País', value: p.country || '—' },
                  { label: 'Moneda', value: p.currency },
                  { label: 'M² totales', value: p.totalM2 > 0 ? `${formatMoney(p.totalM2)} m²` : '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-sm font-medium text-foreground mt-0.5 truncate">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── PROJECTS LIST VIEW ────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function ProjectListView({
  projects,
  onSelectProject,
  onRefresh,
}: {
  projects: ProjectMonitoringItem[];
  onSelectProject: (p: ProjectMonitoringItem) => void;
  onRefresh: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<'name' | 'budget' | 'time' | 'units' | 'alerts'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);

  const totalAlerts = projects.reduce((s, p) => s + p.alerts.length, 0);

  // Filter
  const filtered = projects.filter((p) => {
    const term = searchTerm.toLowerCase();
    return (
      p.name.toLowerCase().includes(term) ||
      (p.workCode?.toLowerCase().includes(term)) ||
      (p.client?.toLowerCase().includes(term)) ||
      (p.city?.toLowerCase().includes(term))
    );
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    switch (sortColumn) {
      case 'name': cmp = a.name.localeCompare(b.name); break;
      case 'budget': cmp = a.budgetUsedPct - b.budgetUsedPct; break;
      case 'time': cmp = a.timeProgressPct - b.timeProgressPct; break;
      case 'units': cmp = a.soldFUPct - b.soldFUPct; break;
      case 'alerts': cmp = a.alerts.length - b.alerts.length; break;
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  // Pagination
  const totalPages = Math.ceil(sorted.length / ROWS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageItems = sorted.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  useEffect(() => { setPage(1); }, [searchTerm]);

  const handleSort = (col: typeof sortColumn) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const SortHeader = ({ col, label, className: cls }: { col: typeof sortColumn; label: string; className?: string }) => (
    <th className={cn('px-4 py-3', cls)}>
      <button
        onClick={() => handleSort(col)}
        className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
      >
        {label}
        <ArrowUpDown className={cn('w-4 h-4', sortColumn === col && 'text-primary')} />
      </button>
    </th>
  );

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Control de Proyectos</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {projects.length} proyecto{projects.length !== 1 ? 's' : ''} activos
                  {totalAlerts > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-destructive font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" /> {totalAlerts} alerta{totalAlerts !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={onRefresh} className="flex items-center gap-2 rounded-lg">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          </div>

          {/* Search */}
          <div className="w-full md:w-72 md:ml-auto">
            <div className="relative">
              <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar proyectos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {sorted.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium text-foreground">
                {searchTerm ? 'Sin resultados' : 'No hay proyectos'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Probá con otro término de búsqueda' : 'Creá un proyecto para ver su monitoreo aquí.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <SortHeader col="name" label="Proyecto" className="text-left" />
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ubicación</th>
                  <SortHeader col="budget" label="Presupuesto" className="text-left" />
                  <SortHeader col="time" label="Tiempo" className="text-left" />
                  <SortHeader col="units" label="UF Vendidas" className="text-left" />
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">OC / Facturas</th>
                  <SortHeader col="alerts" label="Alertas" className="text-center" />
                  <th className="px-4 py-3 text-sm font-medium text-center text-muted-foreground">Acción</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((p, idx) => {
                  const sym = currencySymbol(p.currency);
                  const isLast = idx === pageItems.length - 1;
                  return (
                    <tr
                      key={p.id}
                      className={cn(
                        'transition-colors hover:bg-muted/30 cursor-pointer',
                        !isLast && 'border-b border-border'
                      )}
                      onClick={() => onSelectProject(p)}
                    >
                      {/* Proyecto */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                            <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-foreground">{p.name}</span>
                            <span className="text-xs text-muted-foreground">{p.client || 'Sin cliente'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Ubicación */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">{p.city || '—'}</span>
                          <span className="text-xs text-muted-foreground">{p.country || ''}</span>
                        </div>
                      </td>

                      {/* Presupuesto */}
                      <td className="px-4 py-3">
                        <div className="space-y-1.5 w-36">
                          <div className="flex justify-between text-xs">
                            <span className={cn('font-semibold tabular-nums', p.budgetExceeded ? 'text-destructive' : 'text-foreground')}>
                              {p.budgetUsedPct}%
                            </span>
                            <span className="text-muted-foreground tabular-nums">{sym} {formatMoney(p.budget)}</span>
                          </div>
                          <ProgressBar
                            value={p.budgetUsedPct}
                            colorClass={p.budgetExceeded ? 'bg-destructive' : p.budgetUsedPct >= 80 ? 'bg-warning' : 'bg-success'}
                            height="h-1.5"
                          />
                        </div>
                      </td>

                      {/* Tiempo */}
                      <td className="px-4 py-3">
                        <div className="space-y-1.5 w-32">
                          <div className="flex justify-between text-xs">
                            <span className={cn('font-semibold tabular-nums', p.overdue ? 'text-destructive' : 'text-foreground')}>
                              {p.timeProgressPct}%
                            </span>
                            <span className="text-muted-foreground">
                              {p.overdue ? 'Vencido' : `${p.daysRemaining}d`}
                            </span>
                          </div>
                          <ProgressBar
                            value={p.timeProgressPct}
                            colorClass={p.overdue ? 'bg-destructive' : p.timeProgressPct >= 80 ? 'bg-warning' : 'bg-primary'}
                            height="h-1.5"
                          />
                        </div>
                      </td>

                      {/* UF Vendidas */}
                      <td className="px-4 py-3">
                        <div className="space-y-1.5 w-28">
                          <div className="flex justify-between text-xs">
                            <span className="font-semibold tabular-nums">{p.soldFU}/{p.totalFU}</span>
                            <span className="text-muted-foreground tabular-nums">{p.soldFUPct}%</span>
                          </div>
                          <ProgressBar value={p.soldFUPct} colorClass="bg-success" height="h-1.5" />
                        </div>
                      </td>

                      {/* OC / Facturas */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <ShoppingCart className="w-3 h-3" /> {p.totalPO} OC
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <FileText className="w-3 h-3" /> {p.totalInvoices} fact.
                            {p.pendingInvoices > 0 && (
                              <span className="text-warning font-medium">({p.pendingInvoices} pend.)</span>
                            )}
                          </span>
                        </div>
                      </td>

                      {/* Alertas */}
                      <td className="px-4 py-3 text-center">
                        {p.alerts.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {p.alerts.length}
                          </span>
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-success mx-auto" />
                        )}
                      </td>

                      {/* Acción */}
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectProject(p);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer / Pagination */}
        {sorted.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Mostrando{' '}
              <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
              <span className="font-semibold text-foreground">{sorted.length}</span>{' '}
              proyecto{sorted.length !== 1 ? 's' : ''}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export default function ProjectMonitoring() {
  const [projects, setProjects] = useState<ProjectMonitoringItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<ProjectMonitoringItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectMonitoringService.getMonitoring();
      setProjects(data.projects);
      // If we have a selected project, refresh its data
      if (selectedProject) {
        const updated = data.projects.find((p) => p.id === selectedProject.id);
        if (updated) setSelectedProject(updated);
      }
    } catch {
      showError('No se pudo cargar el monitoreo de proyectos.');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingScreen message="Cargando monitoreo de proyectos" />;

  // ── Detail view ──
  if (selectedProject) {
    return (
      <ProjectDetailView
        p={selectedProject}
        onBack={() => setSelectedProject(null)}
      />
    );
  }

  // ── List view ──
  return (
    <ProjectListView
      projects={projects}
      onSelectProject={(p) => setSelectedProject(p)}
      onRefresh={load}
    />
  );
}
